// pages/EcommerceProductDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import Header from '../components/layout/Header';
import NavigationButton from '../components/layout/Navigation';
import { formatCurrency } from '../utils/formatters';
import UserProfileModal from '../components/modals/UserProfileModal';
import { getMediaUrl } from '../utils/mediaUtils';
import { toggleLikeProduct } from '../services/productApi';
import { createStoreChat } from '../services/chatApi';
import ShareButton from '../components/campaigns/ShareButton';
import { getNextDateFromDays } from '../utils/dateUtils';
import '../styles/Body.css';


function getCsrfToken() {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') {
      return value;
    }
  }
  return null;
}

const getTimeElapsed = (createdAt) => {
  const createdDate = new Date(createdAt);
  const now = new Date();
  const timeDifference = now - createdDate;

  const seconds = Math.floor(timeDifference / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} hari lalu`;
  } else if (hours > 0) {
    return `${hours} jam lalu`;
  } else if (minutes > 0) {
    return `${minutes} menit lalu`;
  } else {
    return `${seconds} detik lalu`;
  }
};

const formatIDR = (amount) => {
  return 'Rp ' + formatCurrency(amount);
};

const EcommerceProductDetail = () => {
  const { slug } = useParams(); // Get the slug from the URL
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('description'); // State to manage active tab
  const [quantity, setQuantity] = useState(1); // State to manage product quantity
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showFullTestimonies, setShowFullTestimonies] = useState({});
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [isCartAnimating, setIsCartAnimating] = useState(false);
  const [liking, setLiking] = useState(false);

  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (!product) return;
    const currentStock = selectedVariation ? selectedVariation.stock : (product.total_stock || product.stock);
    if (quantity > currentStock) {
      setQuantity(currentStock > 0 ? 1 : 0);
    }
  }, [selectedVariation, product]);

  useEffect(() => {
    const fetchProductDetail = async () => {
      try {
        // Fetch product details
        const productResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/products/${slug}/`);
        setProduct(productResponse.data);
        setIsLiked(productResponse.data.is_liked);
        setLikesCount(productResponse.data.likes_count);

      } catch (err) {
        console.error('Error fetching product details:', err);
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetail();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);

  useEffect(() => {
    if (!product) return;
    const fetchRecommendations = async () => {
      try {
        setLoadingRecommendations(true);
        const res = await axios.get(`${baseUrl}/api/products/`);
        const all = res.data || [];
        const currentProdId = String(product.id);
        const currentProdSlug = String(product.slug || '');
        const currentCategory = String(product.category || '').toLowerCase().trim();

        const others = all.filter(p => String(p.id) !== currentProdId && String(p.slug || '') !== currentProdSlug);

        const sameCategory = others.filter(p => {
          if (!currentCategory || !p.category) return false;
          return String(p.category).toLowerCase().trim() === currentCategory;
        });

        const diffCategory = others.filter(p => {
          if (!currentCategory || !p.category) return true;
          return String(p.category).toLowerCase().trim() !== currentCategory;
        });

        const shuffle = (arr) => {
          const copy = [...arr];
          for (let i = copy.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [copy[i], copy[j]] = [copy[j], copy[i]];
          }
          return copy;
        };

        const result = [...shuffle(sameCategory), ...shuffle(diffCategory)].slice(0, 8);
        setRecommendedProducts(result);
      } catch (err) {
        console.error('Error fetching recommended products:', err);
      } finally {
        setLoadingRecommendations(false);
      }
    };
    fetchRecommendations();
  }, [product?.id, product?.slug, product?.category, baseUrl]);

  const [isAddingCart, setIsAddingCart] = useState(false);
  const [isBuyingNow, setIsBuyingNow] = useState(false);

  const [isStartingChat, setIsStartingChat] = useState(false);

  const handleChatSeller = async () => {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      alert('Silakan login terlebih dahulu untuk chat dengan penjual');
      navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    const currentUser = JSON.parse(userStr);
    const sellerId = product.seller?.id || product.seller;
    if (sellerId && sellerId === currentUser.id) {
      alert('Ini adalah produk dari toko Anda sendiri');
      return;
    }

    setIsStartingChat(true);
    try {
      const res = await createStoreChat(product.id, sellerId, `Halo, saya tertarik dengan produk *${product.title}*. Apakah stoknya masih tersedia?`);
      if (window.innerWidth >= 1024) {
        window.dispatchEvent(new CustomEvent('openDesktopChat', { 
          detail: { session: res.data, sessionId: res.data.id } 
        }));
      } else {
        navigate(`/chat/${res.data.id}`);
      }
    } catch (err) {
      console.error('Failed to start store chat:', err);
      alert(err?.response?.data?.error || 'Gagal memulai chat dengan penjual. Silakan coba lagi.');
    } finally {
      setIsStartingChat(false);
    }
  };


  const handleWhatsAppChat = () => {
    const phone = product.seller_phone;
    if (!phone) {
      alert('Nomor WhatsApp penjual belum tersedia. Anda dapat menggunakan fitur Chat Penjual di aplikasi.');
      return;
    }
    let cleanPhone = String(phone).replace(/\D/g, '');
    if (cleanPhone.startsWith('0')) {
      cleanPhone = '62' + cleanPhone.slice(1);
    }
    const msg = encodeURIComponent(`Halo Penjual Barakah Economy, saya tertarik dengan produk *${product.title}* (${window.location.href}). Apakah stoknya masih tersedia?`);
    window.open(`https://wa.me/${cleanPhone}?text=${msg}`, '_blank');
  };

  const addToCart = async () => {
    if (isAddingCart) return;
    const currentStock = selectedVariation ? selectedVariation.stock : (product?.total_stock || product?.stock || 0);
    if (currentStock <= 0) {
      alert('Maaf, stok produk ini sedang habis.');
      return;
    }
    if (product.variations && product.variations.length > 0 && !selectedVariation) {
        alert('Silakan pilih variasi terlebih dahulu (misal: Warna/Ukuran)');
        return;
    }


    const csrfToken = getCsrfToken();
    try {
      setIsAddingCart(true);
      const user = JSON.parse(localStorage.getItem('user'));
      if (!user || !user.access) {
        navigate('/login');
        return;
      }

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/carts/cart/`, {
        product_id: product.id,
        variation_id: selectedVariation?.id || null,
        quantity: quantity
      }, {
        headers: {
          Authorization: `Bearer ${user.access}`,
          'X-CSRFToken': csrfToken,
        }
      });

      // Trigger Cart Pop & Flying Micro-animation
      setIsCartAnimating(true);
      setTimeout(() => setIsCartAnimating(false), 1000);

      window.dispatchEvent(new CustomEvent('cartUpdated', {
        detail: { showToast: true, openDrawer: true, title: product?.title || 'Produk' }
      }));
    } catch (error) {
      console.error('Error adding product to cart:', error);
      const msg = error.response?.data?.error || 'Gagal menambahkan produk ke keranjang';
      alert(msg);
    } finally {
      setIsAddingCart(false);
    }
  };

  const handleBuyNow = async () => {
    if (isBuyingNow) return;
    const currentStock = selectedVariation ? selectedVariation.stock : (product?.total_stock || product?.stock || 0);
    if (currentStock <= 0) {
      alert('Maaf, stok produk ini sedang habis.');
      return;
    }
    if (product?.variations && product.variations.length > 0 && !selectedVariation) {
        alert('Silakan pilih variasi terlebih dahulu (misal: Warna/Ukuran)');
        return;
    }

    const csrfToken = getCsrfToken();
    try {
      setIsBuyingNow(true);
      const user = JSON.parse(localStorage.getItem('user'));
      if (!user || !user.access) {
        navigate('/login');
        return;
      }

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/carts/cart/`, {
        product_id: product.id,
        variation_id: selectedVariation?.id || null,
        quantity: quantity
      }, {
        headers: {
          Authorization: `Bearer ${user.access}`,
          'X-CSRFToken': csrfToken,
        }
      });

      window.dispatchEvent(new CustomEvent('cartUpdated', {
        detail: { showToast: true, title: product?.title || 'Produk' }
      }));

      // Directly navigate to checkout
      navigate('/ecommerce/checkout-sinergy');
    } catch (error) {
      console.error('Error in buy now:', error);
      const msg = error.response?.data?.error || 'Gagal memproses Beli Langsung';
      alert(msg);
    } finally {
      setIsBuyingNow(false);
    }
  };

  const addToWishlist = async (productId) => {
    const csrfToken = getCsrfToken();
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (!user || !user.access) {
        console.error('User not logged in');
        navigate('/login'); // Redirect to login page if not logged in
        return;
      }

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/wishlists/wishlist/`, {
        product_id: productId
      }, {
        headers: {
          Authorization: `Bearer ${user.access}`,
          'X-CSRFToken': csrfToken,
        }
      });

      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      console.error('Error adding product to wishlist:', error);
      alert('Gagal menambahkan ke Incaran, ' + error['response']['data']['message']);
    }
  };

  const handleToggleLike = async () => {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      alert('Anda harus login terlebih dahulu untuk memberikan like.');
      return;
    }

    setLiking(true);
    // Optimistic UI
    const prevIsLiked = isLiked;
    const prevLikesCount = likesCount;
    setIsLiked(!prevIsLiked);
    setLikesCount(prevLikesCount + (prevIsLiked ? -1 : 1));

    try {
      const res = await toggleLikeProduct(product.id);
      setIsLiked(res.data.liked);
      setLikesCount(res.data.likes_count);
    } catch (err) {
      console.error('Error toggling like:', err);
      // Rollback
      setIsLiked(prevIsLiked);
      setLikesCount(prevLikesCount);
    } finally {
      setLiking(false);
    }
  };

  const handleIncrement = () => {
    const currentStock = selectedVariation ? selectedVariation.stock : (product.total_stock || product.stock);
    setQuantity(prevQuantity => Math.min(prevQuantity + 1, currentStock));
  };

  const handleDecrement = () => {
    setQuantity(prevQuantity => Math.max(prevQuantity - 1, 1));
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (error) {
    return <div className="text-center py-8 text-red-500">{error}</div>;
  }

  if (!product) {
    return <div className="text-center py-8">Product not found.</div>;
  }

  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  const toggleTestimoni = (testimoniId) => {
    setShowFullTestimonies((prev) => ({
      ...prev,
      [testimoniId]: !prev[testimoniId],
    }));
  };

  const convertRelativeUrlsToAbsolute = (htmlContent, baseUrl) => {
    // Ensure baseUrl does not have a trailing slash
    if (baseUrl.endsWith('/')) {
      baseUrl = baseUrl.slice(0, -1);
    }
    // Convert relative image URLs to absolute URLs
    return htmlContent.replace(/<img[^>]+src="(\/[^"]+)"[^>]*>/g, (match, src) => {
      return match.replace(src, `${baseUrl}${src}`);
    });
  };

  const renderStars = (count) => {
    const maxStars = 5;
    return (
      <span>
        {[...Array(maxStars)].map((_, i) =>
          i < count ? (
            <span key={i} className="text-xl font-bold text-yellow-400">★</span>
          ) : (
            <span key={i} className="text-xl font-bold text-gray-300">☆</span>
          )
        )}
      </span>
    );
  };

  const showSoldCount = product?.seller_show_sold_count !== undefined && product?.seller_show_sold_count !== null 
    ? Boolean(product.seller_show_sold_count) 
    : true;

  return (
    <div className="body">
      <Helmet>
        <title>{product.title} - Jual Beli Halal & Berkah | Barakah Store</title>
        <meta name="description" content={product.description?.replace(/<[^>]+>/g, '').slice(0, 160) || `Beli ${product.title} produk halal terpercaya di Barakah Store.`} />
        <meta property="og:title" content={`${product.title} - Barakah Store`} />
        <meta property="og:description" content={product.description?.replace(/<[^>]+>/g, '').slice(0, 160) || `Beli ${product.title} di Barakah Store.`} />
        <meta property="og:image" content={product.thumbnail} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={window.location.href} />
        <link rel="canonical" href={window.location.href} />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": product.title,
            "image": [product.thumbnail],
            "description": product.description?.replace(/<[^>]+>/g, '').slice(0, 200),
            "sku": `PROD-${product.id}`,
            "brand": {
              "@type": "Brand",
              "name": "Barakah Economy"
            },
            "offers": {
              "@type": "Offer",
              "url": window.location.href,
              "priceCurrency": "IDR",
              "price": String(product.discounted_price || product.price || 0),
              "availability": (product.stock > 0 || (product.total_stock && product.total_stock > 0)) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
            },
            "aggregateRating": product.testimonies?.length > 0 ? {
              "@type": "AggregateRating",
              "ratingValue": (product.testimonies.reduce((acc, curr) => acc + curr.stars, 0) / product.testimonies.length).toFixed(1),
              "reviewCount": product.testimonies.length
            } : undefined
          })}
        </script>
      </Helmet>

      <Header />

      {/* Product Details */}
      <div className="px-4 py-8 max-w-6xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center text-sm font-bold text-gray-500 hover:text-green-700 transition mb-4">
            <span className="material-icons text-sm mr-1">arrow_back</span> Kembali
        </button>
        <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 flex flex-col md:flex-row">
          <div className="md:w-1/2">
            <div className="relative group">
              <img
                src={getMediaUrl(selectedImage) || getMediaUrl(product.thumbnail) || '/placeholder-image.jpg'}
                alt={product.title}
                className="w-full h-80 md:h-[500px] object-cover transition-all duration-300"
                onError={(e) => {
                  e.target.src = '/placeholder-image.jpg';
                }}
              />
              {product.images && product.images.length > 0 && (
                <div className="flex gap-2 p-4 overflow-x-auto bg-white/80 backdrop-blur-sm absolute bottom-0 left-0 right-0">
                   <div 
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 cursor-pointer flex-shrink-0 transition ${(!selectedImage || selectedImage === product.thumbnail) ? 'border-green-600' : 'border-transparent'}`}
                    onClick={() => setSelectedImage(product.thumbnail)}
                  >
                    <img src={getMediaUrl(product.thumbnail)} className="w-full h-full object-cover" alt="thumb" />
                  </div>
                  {product.images.map((imgObj, idx) => (
                    <div 
                      key={idx} 
                      className={`w-16 h-16 rounded-lg overflow-hidden border-2 cursor-pointer flex-shrink-0 transition ${selectedImage === imgObj.image ? 'border-green-600' : 'border-transparent'}`}
                      onClick={() => setSelectedImage(imgObj.image)}
                    >
                      <img src={getMediaUrl(imgObj.image)} className="w-full h-full object-cover" alt={`gallery-${idx}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="p-6 md:p-10 md:w-1/2 flex flex-col justify-between">
            <div>
              {(() => {
                const basePrice = Number(selectedVariation
                  ? (selectedVariation.additional_price > 0 ? selectedVariation.additional_price : product.price)
                  : product.price);

                let finalPrice = basePrice;
                let hasPromo = false;
                let promoDiscountPct = 0;

                if (product.active_promotion) {
                  const promo = product.active_promotion;
                  if (promo.discount_type === 'percentage') {
                    finalPrice = basePrice - (basePrice * (Number(promo.discount_value) / 100));
                    promoDiscountPct = Number(promo.discount_value);
                    hasPromo = true;
                  } else if (promo.discount_type === 'nominal') {
                    finalPrice = Math.max(0, basePrice - Number(promo.discount_value));
                    promoDiscountPct = basePrice > 0 ? Math.round((Number(promo.discount_value) / basePrice) * 100) : 0;
                    hasPromo = true;
                  } else if (promo.discount_type === 'min_qty_discount' && quantity >= Number(promo.min_quantity || 1)) {
                    if (promo.is_min_qty_percentage) {
                      finalPrice = basePrice - (basePrice * (Number(promo.discount_value) / 100));
                      promoDiscountPct = Number(promo.discount_value);
                    } else {
                      finalPrice = Math.max(0, basePrice - Number(promo.discount_value));
                      promoDiscountPct = basePrice > 0 ? Math.round((Number(promo.discount_value) / basePrice) * 100) : 0;
                    }
                    hasPromo = true;
                  }
                }

                return (
                  <>
                    <div className="flex justify-between items-start gap-4 mb-4">
                      <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{product.title}</h1>
                      <ShareButton 
                        slug={product.slug || product.id} 
                        title={product.title} 
                        type="sinergy"
                        price={hasPromo ? finalPrice : basePrice}
                        originalPrice={hasPromo ? basePrice : null}
                        promoDiscount={hasPromo ? promoDiscountPct : null}
                        description={product.description}
                      />
                    </div>
                    <div className="flex justify-between items-center mb-6">
                      <div className="flex flex-col gap-1">
                        {hasPromo ? (
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="bg-gradient-to-r from-red-600 to-rose-600 text-white text-[10px] font-black px-2.5 py-0.5 rounded-full shadow-sm flex items-center gap-1">
                                <span className="material-icons text-[12px]">local_fire_department</span>
                                {promoDiscountPct > 0 ? `HEMAT ${promoDiscountPct}%` : 'PROMO'}
                              </span>
                              <span className="text-xs font-bold text-rose-600">{product.active_promotion?.title}</span>
                            </div>
                            <div className="flex items-baseline gap-2">
                              <p className="text-2xl md:text-3xl font-black text-emerald-700">
                                Rp {formatCurrency(finalPrice)}
                              </p>
                              <span className="text-sm font-semibold text-gray-400 line-through">
                                Rp {formatCurrency(basePrice)}
                              </span>
                              <span className="text-sm font-semibold text-gray-500">/ {product.unit || 'pcs'}</span>
                            </div>
                          </div>
                        ) : (
                          <p className="text-2xl md:text-3xl font-black text-emerald-700 flex items-baseline gap-1">
                            <span>
                              {selectedVariation 
                                ? formatIDR(selectedVariation.additional_price > 0 ? selectedVariation.additional_price : product.price)
                                : (product.min_price && product.max_price && product.min_price !== product.max_price)
                                  ? `${formatIDR(product.min_price)} ~ ${formatIDR(product.max_price)}`
                                  : formatIDR(product.price)
                              }
                            </span>
                            <span className="text-sm font-semibold text-gray-500">/ {product.unit || 'pcs'}</span>
                          </p>
                        )}
                      </div>
                      <button
                        onClick={handleToggleLike}
                        className={`p-2 rounded-full border ${isLiked ? 'border-red-500 bg-red-50 text-red-500' : 'border-gray-200 text-gray-400 hover:text-red-500'} transition`}
                        title="Sukai produk"
                      >
                        <span className="material-icons text-xl">{isLiked ? 'favorite' : 'favorite_border'}</span>
                      </button>
                    </div>
                  </>
                );
              })()}
                    <div className="flex items-center text-gray-400 text-xs gap-3 mb-4 flex-wrap">
                        <div className="flex items-center gap-1">
                          <span className="material-icons text-sm">visibility</span>
                          {product.views_count || 0} kali dilihat
                        </div>
                        {showSoldCount && (
                          <>
                            <span className="text-gray-300">•</span>
                            <div 
                              className="flex items-center gap-1 font-bold text-gray-800"
                              title={`${product.store_sold_count || 0} terjual via Toko, ${product.charity_sold_count || 0} terjual via Charity/Waqaf`}
                            >
                              <span className="material-icons text-sm text-emerald-600">shopping_bag</span>
                              <span>{product.sold_count || 0} Terjual</span>
                              {product.charity_sold_count > 0 && (
                                <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200/60 ml-1">
                                  Waqaf: {product.charity_sold_count}
                                </span>
                              )}
                            </div>
                          </>
                        )}
                        <span className="text-gray-300">•</span>
                        <div className="flex items-center gap-1">
                          <div 
                            className="flex items-center gap-2 font-bold text-green-700 hover:bg-green-50 p-1.5 rounded-xl transition-all cursor-pointer border border-transparent hover:border-green-100"
                            onClick={() => {
                              const targetStore = product.seller_shop_name || product.seller_name;
                              if (targetStore) {
                                navigate(`/store/${targetStore}`);
                              } else {
                                setSelectedUserId(product.seller);
                                setIsProfileModalOpen(true);
                              }
                            }}
                            title={`Kunjungi Toko ${product.seller_shop_name || (product.seller_name ? `@${product.seller_name}` : '')}`}
                          >
                            <img 
                              src={getMediaUrl(product.seller_avatar) || `https://ui-avatars.com/api/?name=${encodeURIComponent(product.seller_shop_name || product.seller_name || 'Store')}&background=random`} 
                              alt={product.seller_shop_name || product.seller_name} 
                              className="w-6 h-6 rounded-full object-cover border-2 border-white shadow-sm" 
                            />
                            <span>@{product.seller_shop_name || product.seller_name}</span>
                          </div>
                        </div>
                    </div>
                <div className="flex items-center gap-2.5 mb-6 flex-wrap">
                  <p className="text-sm font-medium text-gray-600 bg-gray-100 px-3.5 py-1.5 rounded-full border border-gray-200 inline-flex items-center gap-1.5">
                    Stok: <span className="font-bold text-gray-900">{selectedVariation ? selectedVariation.stock : (product.total_stock || product.stock)}</span> {product.unit || 'pcs'}
                  </p>
                  {showSoldCount && (
                    <p 
                      className="text-sm font-medium text-emerald-800 bg-emerald-50 px-3.5 py-1.5 rounded-full border border-emerald-200 inline-flex items-center gap-1.5"
                      title={`${product.store_sold_count || 0} terjual langsung di toko, ${product.charity_sold_count || 0} terjual lewat program charity / waqaf`}
                    >
                      <span className="material-icons text-emerald-600 text-sm">verified</span>
                      Terjual: <span className="font-bold text-emerald-900">{product.sold_count || 0}</span> {product.unit || 'pcs'}
                    </p>
                  )}
                  {product?.is_preorder && (
                    <p className="text-sm font-semibold text-amber-800 bg-amber-50 px-3.5 py-1.5 rounded-full border border-amber-200 inline-flex items-center gap-1.5 shadow-2xs">
                      <span className="material-icons text-amber-600 text-sm">hourglass_top</span>
                      Pre-Order ({product.preorder_type === 'days' || product.preorder_days ? (product.preorder_duration || `Hari ${product.preorder_days}`) : (product.preorder_duration || (product.preorder_days_min && product.preorder_days_max ? `${product.preorder_days_min} - ${product.preorder_days_max} Hari` : `${product.preorder_days_min || product.preorder_days_max || 7} Hari`))})
                    </p>
                  )}
                </div>

                {product?.variations && product.variations.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-800 mb-2">Pilih Variasi {selectedVariation ? `: ${selectedVariation.name}` : ''}</h3>
                    <div className="flex flex-wrap gap-2">
                      {product.variations.map(variant => (
                        <button 
                          key={variant.id} 
                          onClick={() => setSelectedVariation(variant)}
                          className={`px-4 py-2 border rounded-xl font-medium text-sm transition ${selectedVariation?.id === variant.id ? 'border-green-600 bg-green-50 text-green-700' : 'border-gray-200 hover:border-green-500 hover:text-green-700'}`}
                        >
                          {variant.name} {variant.additional_price > 0 && `(${formatIDR(variant.additional_price)})`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

            {(() => {
              const currentStock = selectedVariation ? selectedVariation.stock : (product.total_stock || product.stock);
              const isOutOfStock = currentStock <= 0;

              return (
                <div className="mt-auto">
                  <div className="flex items-center gap-4 mb-8">
                    <span className="text-sm font-medium text-gray-700">Jumlah:</span>
                    <div className="flex items-center border border-gray-200 rounded-xl px-2 py-1">
                      <button
                        onClick={handleDecrement}
                        className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-green-700 hover:bg-green-50 rounded-lg transition"
                        disabled={quantity <= 1 || isOutOfStock}
                      >
                        <span className="material-icons text-lg">remove</span>
                      </button>
                      <span className="w-12 text-center font-bold text-gray-800">{isOutOfStock ? 0 : quantity}</span>
                      <button
                        onClick={handleIncrement}
                        className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-green-700 hover:bg-green-50 rounded-lg transition"
                        disabled={quantity >= currentStock || isOutOfStock}
                      >
                        <span className="material-icons text-lg">add</span>
                      </button>
                    </div>
                  </div>

                  {/* Row Tombol Chat Penjual & WhatsApp Langsung */}
                  <div className="flex gap-2.5 mb-4">
                    <button
                      type="button"
                      onClick={handleChatSeller}
                      disabled={isStartingChat}
                      className="flex-1 py-2.5 px-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-200 flex items-center justify-center text-center gap-1.5 transition active:scale-95 disabled:opacity-50 min-h-[42px]"
                    >
                      {isStartingChat ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <>
                          <span className="material-icons text-base shrink-0">chat</span>
                          <span className="leading-tight text-center whitespace-nowrap sm:whitespace-normal">Chat Penjual</span>
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={handleWhatsAppChat}
                      className="flex-1 py-2.5 px-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-200 flex items-center justify-center text-center gap-1.5 transition active:scale-95 min-h-[42px]"
                    >
                      <svg className="w-4 h-4 fill-current shrink-0" viewBox="0 0 24 24">
                        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.888-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.347-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.876 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                      </svg>
                      <span className="leading-tight text-center whitespace-nowrap sm:whitespace-normal">Tanya via WA</span>
                    </button>
                  </div>


                  <div className="flex flex-col md:flex-row gap-4">
                    <button 
                      className={`flex-1 py-3 px-6 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg transition-all transform ${isOutOfStock || isAddingCart ? 'bg-gray-200 text-gray-500 cursor-not-allowed shadow-none' : 'bg-green-600 hover:bg-green-700 text-white shadow-green-200 hover:-translate-y-1'} ${isCartAnimating ? 'animate-bounce ring-4 ring-green-300 scale-105' : ''}`}
                      onClick={addToCart}
                      disabled={isOutOfStock || isAddingCart}
                    >
                      {isAddingCart ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                          <span>Menambahkan...</span>
                        </span>
                      ) : (
                        <>
                          <span className="material-icons text-xl">{isOutOfStock ? 'remove_shopping_cart' : 'shopping_cart'}</span>
                          {isOutOfStock ? 'Stok Habis' : 'Keranjang'}
                        </>
                      )}
                    </button>
                    <button 
                      className={`px-4 py-3 border-2 transition-all rounded-xl flex items-center justify-center gap-1.5 active:scale-90 shadow-sm ${isLiked ? 'border-red-500 bg-red-50 text-red-600' : 'border-gray-200 text-gray-400 hover:border-red-200 hover:text-red-400'}`}
                      onClick={handleToggleLike}
                      disabled={liking}
                    >
                      <span className="material-icons text-xl">{isLiked ? 'favorite' : 'favorite_border'}</span>
                      <span className="text-xs font-bold">{likesCount}</span>
                    </button>

                    <button 
                      className={`flex-[2] py-3 px-6 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg transition-all transform mt-4 md:mt-0 ${isOutOfStock || isBuyingNow ? 'bg-gray-200 text-gray-500 cursor-not-allowed shadow-none' : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-200 hover:-translate-y-1'}`}
                      onClick={handleBuyNow}
                      disabled={isOutOfStock || isBuyingNow}
                    >
                      {isBuyingNow ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                          <span>Memproses...</span>
                        </span>
                      ) : (
                        <>
                          <span className="material-icons text-xl">{isOutOfStock ? 'block' : 'shopping_bag'}</span>
                          {isOutOfStock ? 'Habis' : 'Beli Langsung'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Toko / Seller Profile Card (Marketplace Style) */}
      <div className="px-4 max-w-6xl mx-auto mt-6 mb-4">
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="relative shrink-0">
              <img 
                src={getMediaUrl(product.seller_avatar) || `https://ui-avatars.com/api/?name=${encodeURIComponent(product.seller_shop_name || product.seller_name || 'Store')}&background=random`} 
                alt={product.seller_shop_name || product.seller_name} 
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl object-cover border-2 border-emerald-100 shadow-sm" 
              />
              <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 border-2 border-white rounded-full flex items-center justify-center text-white shadow-xs" title="Official Toko">
                <span className="material-icons text-[10px]">check</span>
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-bold text-gray-900 leading-tight">
                  Toko {product.seller_shop_name || (product.seller_name ? `@${product.seller_name}` : '')}
                </h2>
                <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Official Toko
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500 flex-wrap">
                {product.seller_city_name && (
                  <span className="flex items-center gap-1 text-gray-600 font-medium">
                    <span className="material-icons text-sm text-rose-500">location_on</span>
                    {product.seller_city_name}
                  </span>
                )}
                {showSoldCount && (
                  <span className="flex items-center gap-1 text-emerald-700 font-semibold">
                    <span className="material-icons text-sm">verified</span>
                    {product.sold_count || 0} Terjual
                  </span>
                )}
                {product.seller_followers_count !== undefined && (
                  <span className="flex items-center gap-1 text-gray-500 font-semibold">
                    <span className="material-icons text-sm text-gray-400">group</span>
                    {product.seller_followers_count} Pengikut
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap self-end md:self-auto w-full md:w-auto">
            <Link
              to={`/store/${product.seller_shop_name || product.seller_name}`}
              className="flex-1 md:flex-none px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition active:scale-95 shadow-sm shadow-emerald-200"
            >
              <span className="material-icons text-base">storefront</span>
              <span>Kunjungi Toko</span>
            </Link>

            <button
              type="button"
              onClick={handleChatSeller}
              className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold flex items-center justify-center transition active:scale-95"
              title="Chat Penjual"
            >
              <span className="material-icons text-base">chat</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mt-4 px-4 max-w-6xl mx-auto pb-20">
        <div className="flex justify-start gap-4 sm:gap-8 bg-white border-b px-4 sm:px-6 overflow-x-auto">
          <button
            className={`py-2 px-4 text-sm font-medium ${activeTab === 'description' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('description')}
          >
            Keterangan
          </button>
          <button
            className={`py-2 px-4 text-sm font-medium ${activeTab === 'testimonies' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('testimonies')}
          >
            Testimoni ({product.testimonies ? product.testimonies.length : 0})
          </button>
        </div>

        {/* Tab Content */}
        <div className="mt-4">
          {activeTab === 'description' && (
            <div className="space-y-6">
              {/* Informasi Operasional Toko, PO & Jadwal Pengantaran (Jika diaktifkan penjual) */}
              {(product?.is_operational_hours_active || product?.is_preorder || product?.is_delivery_schedule_active || product?.out_of_po_shipping_active || product?.is_shipping_cost_active || product?.allow_delivery_timing_choice) && (
                <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-slate-50 to-gray-50/80 border border-slate-200 shadow-xs space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                      <span className="material-icons text-base text-emerald-600">storefront</span>
                      Layanan & Jadwal Toko
                    </span>
                    {product?.is_preorder && (
                      <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase tracking-wide">
                        Pre-Order
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {/* Jam Operasional */}
                    {product?.is_operational_hours_active && (
                      <div className="flex items-start gap-2.5 p-3 rounded-xl bg-white border border-slate-200/80 shadow-2xs">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                          <span className="material-icons text-lg">schedule</span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold text-slate-400 leading-tight">Jam Operasional</p>
                          <p className="text-xs font-bold text-slate-800 mt-0.5">{product.operational_hours || 'Buka setiap hari'}</p>
                        </div>
                      </div>
                    )}

                    {/* Waktu Pre-Order */}
                    {product?.is_preorder && (() => {
                      const poDateInfo = product.preorder_days ? getNextDateFromDays(product.preorder_days) : null;
                      return (
                        <div className="flex items-start gap-2.5 p-3 rounded-xl bg-white border border-amber-200/80 shadow-2xs">
                          <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                            <span className="material-icons text-lg">hourglass_top</span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-semibold text-amber-600/90 leading-tight">Waktu Pre-Order (PO)</p>
                            <p className="text-xs font-bold text-slate-800 mt-0.5">
                              {product.preorder_type === 'days' || product.preorder_days ? (
                                <span>Setiap hari: <strong className="text-amber-800">{product.preorder_days}</strong></span>
                              ) : (
                                product.preorder_duration || (
                                  product.preorder_days_min && product.preorder_days_max
                                    ? `${product.preorder_days_min} - ${product.preorder_days_max} Hari`
                                    : `${product.preorder_days_min || product.preorder_days_max || 7} Hari`
                                )
                              )}
                            </p>
                            {poDateInfo && (
                              <p className="text-[11px] text-amber-900 font-semibold mt-1 bg-amber-50 px-2 py-0.5 rounded border border-amber-200/80 inline-flex items-center gap-1">
                                <span className="material-icons text-[13px] text-amber-600">event</span>
                                <span>Jadwal PO Terdekat: <strong className="underline">{poDateInfo.display}</strong></span>
                              </p>
                            )}
                            <p className="text-[10px] text-slate-400 mt-0.5">Pesanan diproses sesuai jadwal PO</p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Ongkir Toko: Flat vs Sesuai Jarak */}
                    {(product?.out_of_po_shipping_active || product?.is_shipping_cost_active || product?.shipping_cost_type === 'distance') && (() => {
                      const isDistance = product?.shipping_cost_type === 'distance' || (product?.out_of_po_shipping_active && product?.out_of_po_shipping_type === 'distance');
                      return (
                        <div className={`flex items-start gap-2.5 p-3 rounded-xl bg-white shadow-2xs ${isDistance ? 'border border-blue-200/90' : 'border border-purple-200/80'}`}>
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isDistance ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`}>
                            <span className="material-icons text-lg">{isDistance ? 'near_me' : 'local_atm'}</span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className={`text-[11px] font-semibold leading-tight ${isDistance ? 'text-blue-700' : 'text-purple-700'}`}>
                                {isDistance ? 'Ongkir Sesuai Jarak' : 'Ongkir Flat Toko'}
                              </p>
                              <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${isDistance ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                                {isDistance ? 'Dikonfirmasi Kurir/Admin' : 'Flat Beli Banyak'}
                              </span>
                            </div>
                            <p className="text-xs font-bold text-slate-800 mt-0.5">
                              {isDistance
                                ? 'Dikonfirmasi Penjual / Kurir'
                                : (Number(product.out_of_po_shipping_active && product.out_of_po_shipping_cost ? product.out_of_po_shipping_cost : product.shipping_cost) > 0
                                    ? `Rp ${new Intl.NumberFormat('id-ID').format(product.out_of_po_shipping_active && product.out_of_po_shipping_cost ? product.out_of_po_shipping_cost : product.shipping_cost)}`
                                    : 'Bebas Ongkir')}
                            </p>
                            <p className="text-[10px] text-slate-400 mt-0.5">
                              {isDistance
                                ? 'Biaya ongkir dihitung sesuai jarak alamat dan akan dikabari langsung oleh pihak Admin / Seller / Kurir'
                                : 'Tarif tetap satu harga walau membeli banyak produk dari toko ini'}
                            </p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Jadwal / Tanggal Pengantaran */}
                    {product?.is_delivery_schedule_active && (
                      <div className="flex items-start gap-2.5 p-3 rounded-xl bg-white border border-emerald-200/80 shadow-2xs">
                        <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                          <span className="material-icons text-lg">local_shipping</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-semibold text-emerald-700 leading-tight">Jadwal & Pengantaran</p>
                          <p className="text-xs font-bold text-slate-800 mt-0.5">
                            {product.delivery_schedule_type === 'range' && (
                              <span>Estimasi Rentang {product.delivery_range_min || 1} - {product.delivery_range_max || 3} Hari Kerja</span>
                            )}
                            {product.delivery_schedule_type === 'days' && (() => {
                              const delDateInfo = product.delivery_days ? getNextDateFromDays(product.delivery_days) : null;
                              return (
                                <div>
                                  <span>Pengiriman pada hari: <strong className="text-emerald-800">{product.delivery_days || 'Setiap Hari'}</strong></span>
                                  {delDateInfo && (
                                    <span className="block text-[11px] text-emerald-800 font-semibold mt-0.5">
                                      Pengantaran Terdekat: <strong className="underline">{delDateInfo.display}</strong>
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                            {product.delivery_schedule_type === 'date' && (
                              <span>Tanggal Pengiriman: <strong className="text-emerald-800">{product.delivery_date ? new Date(product.delivery_date).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</strong></span>
                            )}
                          </p>
                          {product.delivery_note && (
                            <p className="text-[11px] text-slate-500 mt-1 italic flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-md border border-slate-100">
                              <span className="material-icons text-[13px] text-slate-400">info_outline</span>
                              {product.delivery_note}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Opsi Fleksibel Pilihan Pengiriman */}
                    {product?.allow_delivery_timing_choice && (
                      <div className="flex items-start gap-2.5 p-3 rounded-xl bg-white border border-teal-200/80 shadow-2xs sm:col-span-2">
                        <div className="w-8 h-8 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
                          <span className="material-icons text-lg">calendar_month</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-semibold text-teal-700 leading-tight">Fleksibilitas Pengiriman</p>
                          <p className="text-xs font-bold text-slate-800 mt-0.5">
                            Bisa pilih: <span className="text-emerald-700">Ikut Jadwal Minggu Ini</span> atau <span className="text-purple-700">Kirim ke Minggu Depannya</span>
                          </p>
                          <p className="text-[10px] text-slate-400 mt-0.5">Pilihan jadwal pengiriman dapat ditentukan secara fleksibel saat checkout</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Deskripsi Produk */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 text-sm mb-3 flex items-center gap-2">
                  <span className="material-icons text-base text-emerald-600">description</span>
                  <span>Deskripsi Lengkap Produk</span>
                </h3>
                {product.description ? (
                  <div>
                    <div 
                      className={`rich-text-content relative transition-all duration-300 ${!showFullDescription && product.description.length > 400 ? 'max-h-72 overflow-hidden' : ''}`}
                      dangerouslySetInnerHTML={{
                        __html: convertRelativeUrlsToAbsolute(product.description, baseUrl)
                      }}
                    />
                    {!showFullDescription && product.description.length > 400 && (
                      <div className="absolute -mt-16 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                    )}
                    {product.description.length > 400 && (
                      <div className="mt-4 pt-3 border-t border-gray-100">
                        <button
                          onClick={toggleDescription}
                          className="inline-flex items-center gap-1.5 text-emerald-700 font-bold text-sm hover:text-emerald-800 transition"
                        >
                          <span>{showFullDescription ? 'Tampilkan Lebih Ringkas' : 'Baca Selengkapnya'}</span>
                          <span className="material-icons text-sm">{showFullDescription ? 'expand_less' : 'expand_more'}</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm italic">Belum ada deskripsi untuk produk ini.</p>
                )}
              </div>
            </div>
          )}


          {activeTab === 'testimonies' && (
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              {product.testimonies && product.testimonies.length > 0 ? (
                <div className="space-y-4 divide-y divide-gray-100">
                  {product.testimonies.map((testimoni) => (
                    <div key={testimoni.id} className="pt-4 first:pt-0">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs uppercase">
                            {testimoni.customer ? testimoni.customer.slice(0, 2) : 'PL'}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-gray-900 text-xs">{testimoni.customer}</span>
                              <span className="text-[9px] bg-emerald-50 text-emerald-700 font-bold px-1.5 py-0.5 rounded-full border border-emerald-200">
                                {testimoni.is_admin_entry ? 'Pembeli Terverifikasi' : 'Verified Buyer'}
                              </span>
                            </div>
                            <span className="text-[10px] text-gray-400">
                              {new Date(testimoni.created_at).toLocaleDateString('id-ID', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })}
                            </span>
                          </div>
                        </div>
                        <div className="flex text-amber-400 text-sm">
                          {renderStars(testimoni.stars || 5)}
                        </div>
                      </div>

                      <p className="text-xs text-gray-700 leading-relaxed pl-10 whitespace-pre-line">
                        {testimoni.description}
                      </p>

                      {testimoni.image && (
                        <div className="pl-10 mt-3">
                          <img
                            src={getMediaUrl(testimoni.image)}
                            alt="Foto Ulasan"
                            className="w-20 h-20 sm:w-28 sm:h-28 object-cover rounded-xl border border-gray-200 shadow-sm cursor-pointer hover:opacity-90 transition"
                            onClick={() => window.open(getMediaUrl(testimoni.image), '_blank')}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <span className="material-icons text-4xl text-gray-300">rate_review</span>
                  <p className="text-gray-500 text-xs mt-2">Belum ada testimoni untuk produk ini.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Rekomendasi Produk Lainnya */}
        {recommendedProducts.length > 0 && (
          <div className="mt-14 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-6 bg-emerald-600 rounded-full"></span>
                <h2 className="text-base md:text-xl font-black text-gray-900 tracking-tight">
                  Rekomendasi Produk Lainnya
                </h2>
              </div>
              <Link 
                to="/ecommerce" 
                className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-0.5"
              >
                <span>Lihat Semua Produk</span>
                <span className="material-icons text-sm">chevron_right</span>
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
              {recommendedProducts.map((rec) => {
                const inStock = (rec.total_stock !== undefined ? rec.total_stock : rec.stock) > 0;
                const sellerCity = rec.seller_city_name || 'Indonesia';
                const recSlug = rec.slug || rec.id;

                return (
                  <div 
                    key={rec.id} 
                    className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-gray-100 flex flex-col justify-between group ${!inStock ? 'opacity-75' : ''}`}
                  >
                    <div className="relative">
                      {rec.active_promotion && (
                        <div className="absolute top-2 left-2 z-10 bg-gradient-to-r from-red-600 to-rose-600 text-white text-[10px] font-black px-2 py-0.5 rounded-lg shadow-md flex items-center gap-1">
                          <span className="material-icons text-[11px]">local_fire_department</span>
                          {rec.promo_discount_percentage ? `-${rec.promo_discount_percentage}%` : 'PROMO'}
                        </div>
                      )}
                      {rec.is_preorder && (
                        <div className={`absolute ${rec.active_promotion ? 'top-8' : 'top-2'} left-2 z-10 bg-amber-500/95 text-white text-[9px] font-bold px-2 py-0.5 rounded-lg shadow uppercase tracking-wider`}>
                          PO
                        </div>
                      )}
                      {!inStock && (
                        <div className="absolute top-2 right-10 z-10 bg-gray-900/80 text-white text-[9px] font-black px-2 py-0.5 rounded-lg shadow uppercase">
                          Habis
                        </div>
                      )}
                      <Link to={`/produk/${recSlug}`}>
                        <img
                          src={getMediaUrl(rec.thumbnail) || '/placeholder-image.jpg'}
                          alt={rec.title}
                          className="w-full h-36 md:h-44 object-cover group-hover:scale-105 transition-transform duration-300 bg-gray-50"
                          onError={(e) => { e.target.src = '/placeholder-image.jpg'; }}
                        />
                      </Link>
                      <div className="absolute top-2 right-2 z-10">
                        <ShareButton
                          slug={recSlug}
                          title={rec.title}
                          type="product"
                          price={rec.discounted_price || rec.price}
                          description={rec.description}
                          variant="card-icon"
                        />
                      </div>
                    </div>

                    <div className="p-3.5 flex flex-col flex-1 justify-between">
                      <div>
                        <div className="flex items-center gap-1 text-gray-500 text-[10px] font-medium mb-1">
                          <span className="material-icons text-[12px] text-emerald-600 shrink-0">location_on</span>
                          <span className="truncate max-w-[130px]" title={sellerCity}>
                            {sellerCity}
                          </span>
                        </div>

                        <h3 className="text-xs md:text-sm font-semibold mb-1.5 line-clamp-2 min-h-[36px] text-gray-800 hover:text-emerald-700 transition-colors">
                          <Link to={`/produk/${recSlug}`}>
                            {rec.title}
                          </Link>
                        </h3>

                        <div className="mb-2">
                          {rec.active_promotion && rec.discounted_price ? (
                            <div>
                              <div className="flex items-baseline gap-1.5">
                                <p className="text-emerald-700 font-bold text-sm">
                                  Rp {formatCurrency(rec.discounted_price)}
                                </p>
                                <span className="text-gray-400 line-through text-[11px] font-semibold">
                                  Rp {formatCurrency(rec.price)}
                                </span>
                              </div>
                              <span className="text-[10px] text-gray-400 block">/ {rec.unit || 'pcs'}</span>
                            </div>
                          ) : (
                            <p className="text-emerald-700 font-bold text-sm">
                              {rec.min_price && rec.max_price && rec.min_price !== rec.max_price
                                ? `Rp ${formatCurrency(rec.min_price)} ~ ${formatCurrency(rec.max_price)}`
                                : formatIDR(rec.price)
                              } <span className="text-[10px] text-gray-400">/ {rec.unit || 'pcs'}</span>
                            </p>
                          )}
                          <div className="flex items-center justify-between text-[10px] mt-0.5 text-gray-400">
                            <span>stok: {inStock ? (rec.total_stock !== undefined ? rec.total_stock : rec.stock) : 'habis'}</span>
                            {rec.sold_count > 0 && (
                              <span className="font-bold text-emerald-700 bg-emerald-50 px-1 rounded">
                                {rec.sold_count} terjual
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <Link
                        to={`/produk/${recSlug}`}
                        className="w-full bg-emerald-50 hover:bg-emerald-100 text-emerald-800 py-2 rounded-xl font-bold flex items-center justify-center gap-1 transition-all text-[11px] border border-emerald-200 mt-2"
                      >
                        <span>Lihat Detail</span>
                        <span className="material-icons text-xs">arrow_forward</span>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <NavigationButton />
      <UserProfileModal 
        userId={selectedUserId} 
        isOpen={isProfileModalOpen} 
        onClose={() => setIsProfileModalOpen(false)} 
      />
    </div>
  );
};

export default EcommerceProductDetail;